import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import useReportsCatalog from '../../hooks/useReportsCatalog';
import ReportsFilters from './ReportsFilters';
import ReportsSummaryStrip from './ReportsSummaryStrip';
import ReportsWarningsPanel from './ReportsWarningsPanel';
import ReportsGrid from './ReportsGrid';
import ArchivedReportsDialog from './ArchivedReportsDialog';
import ReportMetadataDialog from './ReportMetadataDialog';

const ReportsCatalog = () => {
  const {
    activeReports,
    archivedReports,
    datasetOptions,
    datasetMap,
    filters,
    setFilters,
    summary,
    reportLogs,
    archivedOpen,
    setArchivedOpen,
    editingReport,
    setEditingReport,
    reportsQuery,
    archiveReport,
    unarchiveReport,
    updateReport,
  } = useReportsCatalog();

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const handleArchive = async (report) => {
    try {
      await archiveReport.mutateAsync(report.id);
      setSnackbar({ open: true, message: `Archived ${report.name}.`, severity: 'success' });
    } catch (_error) {
      setSnackbar({ open: true, message: 'Failed to archive report.', severity: 'error' });
    }
  };

  const handleUnarchive = async (report) => {
    try {
      await unarchiveReport.mutateAsync(report.id);
      setSnackbar({ open: true, message: `Unarchived ${report.name}.`, severity: 'success' });
    } catch (_error) {
      setSnackbar({ open: true, message: 'Failed to unarchive report.', severity: 'error' });
    }
  };

  const handleMetadataSave = async ({ reportId, metadata, changeNote, ifMatchVersion }) => {
    try {
      await updateReport.mutateAsync({ reportId, metadata, changeNote, ifMatchVersion });
      setSnackbar({ open: true, message: 'Report metadata updated.', severity: 'success' });
      setEditingReport(null);
    } catch (_error) {
      setSnackbar({ open: true, message: 'Failed to update metadata.', severity: 'error' });
    }
  };

  const handleCopyLink = async (report) => {
    const url = `${window.location.origin}/reports/${report.id}`;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setSnackbar({ open: true, message: 'Report link copied.', severity: 'success' });
        return;
      }
    } catch (_error) {
      // noop
    }
    setSnackbar({ open: true, message: 'Clipboard access is blocked. Open the report and copy the URL from the address bar.', severity: 'warning' });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Reports
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Central catalog for all reporting definitions (API-driven for now).
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => setArchivedOpen(true)}>
            View archived reports
          </Button>
        </Stack>
      </Paper>

      <ReportsSummaryStrip summary={summary} />

      <ReportsFilters
        filters={filters}
        datasetOptions={datasetOptions}
        onChange={setFilters}
      />

      <ReportsWarningsPanel logs={reportLogs} />

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        {reportsQuery.isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Stack>
        )}
        {reportsQuery.error && (
          <Alert severity="error">
            {reportsQuery.error?.message || 'Failed to load reports.'}
          </Alert>
        )}
        {!reportsQuery.isLoading && !reportsQuery.error && (
          <ReportsGrid
            rows={activeReports}
            datasetMap={datasetMap}
            onArchive={handleArchive}
            onUnarchive={handleUnarchive}
            onEdit={setEditingReport}
            onCopyLink={handleCopyLink}
          />
        )}
      </Paper>

      <ArchivedReportsDialog
        open={archivedOpen}
        reports={archivedReports}
        datasetMap={datasetMap}
        onClose={() => setArchivedOpen(false)}
        onUnarchive={handleUnarchive}
      />

      <ReportMetadataDialog
        open={Boolean(editingReport)}
        report={editingReport}
        onClose={() => setEditingReport(null)}
        onSave={handleMetadataSave}
        saving={updateReport.isPending}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
};

export default ReportsCatalog;
